import { useEffect, useRef, useState } from "react";
import {
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Layers,
  Navigation2,
  Filter,
  Cable,
  Waves,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { CATEGORIES, CATEGORY_HEX, categoryLabel } from "@/lib/maritime";
import api from "@/lib/api";

const LAYERS = [
  { key: "vessels", label: "Vessels", enabled: true },
  { key: "tracks", label: "Historical Tracks", enabled: true },
  { key: "infra", label: "Oil Rigs", enabled: true },
  { key: "slicks", label: "Oil Slicks", enabled: true },
];

// Oil-slick polygon colours by Cerulean likely-source classification.
const SLICK_KEY = [
  { hex: "#FF2A6D", label: "Vessel" },
  { hex: "#F59E0B", label: "Infrastructure" },
  { hex: "#14B8A6", label: "Natural seep" },
  { hex: "#A855F7", label: "Other / ambiguous" },
];

export const LeftPanel = ({
  collapsed,
  setCollapsed,
  filters,
  setFilters,
  layers,
  setLayers,
  vessels,
  counts,
  onSelect,
  onOpenPilot,
}) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!q.trim()) {
      setResults(null);
      return;
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.search(q.trim());
        setResults(r.results);
      } catch {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const toggleCategory = (key) => {
    const next = new Set(filters.categories);
    next.has(key) ? next.delete(key) : next.add(key);
    setFilters({ ...filters, categories: next });
  };

  const list = results !== null ? results : vessels;

  if (collapsed) {
    return (
      <button
        data-testid="left-panel-expand"
        onClick={() => setCollapsed(false)}
        className="absolute top-16 left-4 z-20 flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900/95 backdrop-blur-xl border border-slate-800/80 text-cyan-400 shadow-2xl hover:border-cyan-500/40 transition-colors"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    );
  }

  return (
    <aside
      data-testid="left-panel"
      className="absolute top-16 left-4 bottom-16 z-20 w-80 md:w-96 flex flex-col rounded-xl bg-slate-900/95 backdrop-blur-xl border border-slate-800/80 shadow-2xl overflow-hidden st-fade"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800/80">
        <span className="font-mono text-xs font-semibold uppercase tracking-widest text-cyan-400">
          Fleet Console
        </span>
        <button
          data-testid="left-panel-collapse"
          onClick={() => setCollapsed(true)}
          className="text-slate-500 hover:text-slate-200 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      </div>

      <div data-testid="left-panel-scroll" className="flex-1 min-h-0 overflow-y-auto st-scroll">
      <div className="p-3 space-y-2 border-b border-slate-800/60">
        <div className="flex items-center gap-2 text-[11px] font-mono">
          <Cable className="h-3.5 w-3.5 text-[#818CF8]" />
          <span className="uppercase tracking-widest text-white font-semibold">Critical Infrastructure</span>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-[#6366F1]/45 bg-[#6366F1]/12 px-3 py-2.5">
          <label className="flex items-center gap-2.5 text-sm text-white font-medium cursor-pointer">
            <Switch
              data-testid="layer-toggle-cables"
              checked={!!layers.cables}
              onCheckedChange={(v) => setLayers({ ...layers, cables: v })}
              className="data-[state=checked]:bg-[#6366F1] data-[state=unchecked]:bg-slate-600"
            />
            <span className="flex items-center gap-2">
              <Cable className="h-4 w-4 text-[#A5B4FC]" />
              Major Undersea Cables
            </span>
          </label>
          <span className="h-2.5 w-8 rounded-full shrink-0" style={{ background: "repeating-linear-gradient(90deg,#6366F1 0 5px,transparent 5px 8px)" }} />
        </div>
        {layers.cables && (
          <p data-testid="cable-attribution" className="text-[9px] leading-relaxed font-mono text-slate-500">
            ~10 major systems. Geometry: OpenStreetMap (ODbL) + indicative public landing data. Routes are
            indicative — not for navigation.
          </p>
        )}
        <button
          data-testid="pilot-launch"
          onClick={onOpenPilot}
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-cyan-600/90 hover:bg-cyan-500 text-white px-3 py-2.5 text-sm font-mono font-semibold border border-cyan-400/40 transition-colors"
        >
          <Waves className="h-4 w-4" /> Oil Slick Tracking — Vietnam Pilot
        </button>
      </div>
      <div className="p-3 space-y-3 border-b border-slate-800/60">
        <div className="flex items-center gap-2 text-[11px] font-mono">
          <Filter className="h-3.5 w-3.5 text-slate-500" />
          <span className="uppercase tracking-widest text-slate-500">Ship types</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {CATEGORIES.map((c) => {
            const active = filters.categories.has(c.key);
            return (
              <button
                key={c.key}
                data-testid={`ship-type-filter-${c.key}`}
                onClick={() => toggleCategory(c.key)}
                className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all"
                style={{
                  borderColor: active ? c.hex : "rgba(51,65,85,0.6)",
                  background: active ? `${c.hex}22` : "transparent",
                  color: active ? "#fff" : "#94a3b8",
                }}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: c.hex }}
                />
                {c.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-1">
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <Switch
              data-testid="filter-moving-only"
              checked={filters.movingOnly}
              onCheckedChange={(v) => setFilters({ ...filters, movingOnly: v })}
            />
            Moving only <span className="text-slate-500 font-mono">(&gt;0.5 kn)</span>
          </label>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <Switch
              data-testid="filter-show-stale"
              checked={filters.showStale}
              onCheckedChange={(v) => setFilters({ ...filters, showStale: v })}
            />
            Show stale <span className="text-slate-500 font-mono">(&gt;30 min)</span>
          </label>
        </div>
      </div>

      <div className="p-3 space-y-2 border-b border-slate-800/60">
        <div className="flex items-center gap-2 text-[11px] font-mono">
          <Layers className="h-3.5 w-3.5 text-slate-500" />
          <span className="uppercase tracking-widest text-slate-500">Layers</span>
        </div>
        {LAYERS.map((l) => (
          <div key={l.key} className="flex items-center justify-between">
            <label
              className={`flex items-center gap-2 text-xs ${
                l.enabled ? "text-slate-300" : "text-slate-600"
              }`}
            >
              <Switch
                data-testid={`layer-toggle-${l.key}`}
                disabled={!l.enabled}
                checked={!!layers[l.key]}
                onCheckedChange={(v) => setLayers({ ...layers, [l.key]: v })}
              />
              {l.label}
            </label>
            {!l.enabled && (
              <span className="text-[9px] font-mono uppercase tracking-wider text-slate-600">
                Phase 2
              </span>
            )}
          </div>
        ))}

        {layers.slicks && (
          <div data-testid="slick-color-key" className="pt-1.5 mt-1 border-t border-slate-800/50">
            <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">
              Slick colour = likely source
            </span>
            <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1">
              {SLICK_KEY.map((s) => (
                <div key={s.label} className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: s.hex }} />
                  <span className="text-[11px] text-slate-400 truncate">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-3 py-2 flex items-center justify-between border-b border-slate-800/60">
        <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
          {results !== null ? "Search results" : "Legend / Fleet"}
        </span>
        <span className="font-mono text-xs text-cyan-400 tabular-nums">
          {list.length}
        </span>
      </div>

      {results === null && (
        <div className="px-3 py-2 grid grid-cols-2 gap-1.5 border-b border-slate-800/40">
          {CATEGORIES.map((c) => (
            <div key={c.key} className="flex items-center gap-1.5 text-[11px]">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: c.hex }}
              />
              <span className="text-slate-400 flex-1 truncate">{c.label}</span>
              <span className="font-mono text-slate-500 tabular-nums">
                {counts[c.key] || 0}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="px-3 pt-2 pb-1">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            data-testid="vessel-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, MMSI or IMO"
            className="pl-9 pr-8 bg-slate-800/60 border-slate-700/60 text-sm font-mono placeholder:text-slate-500 focus-visible:ring-cyan-500/40"
          />
          {q && (
            <button
              data-testid="vessel-search-clear"
              onClick={() => setQ("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="st-scroll">
        <div className="p-2 space-y-1">
          {list.slice(0, 250).map((v) => (
            <button
              key={v.mmsi}
              data-testid={`vessel-list-item-${v.mmsi}`}
              onClick={() => onSelect(v.mmsi)}
              className="w-full text-left rounded-lg border border-transparent hover:border-slate-700/60 hover:bg-slate-800/50 px-3 py-2 transition-all group"
            >
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full shrink-0"
                  style={{
                    backgroundColor: v.stale ? "#64748b" : CATEGORY_HEX[v.category],
                  }}
                />
                <span className="text-sm text-slate-200 font-medium truncate flex-1 group-hover:text-white">
                  {v.name || "Unknown vessel"}
                </span>
                {v.sog != null && (
                  <span className="font-mono text-[11px] text-slate-500 tabular-nums flex items-center gap-0.5">
                    <Navigation2
                      className="h-3 w-3"
                      style={{ transform: `rotate(${v.cog || 0}deg)` }}
                    />
                    {Number(v.sog).toFixed(1)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5 pl-4.5">
                <span className="font-mono text-[10px] text-slate-500">
                  MMSI {v.mmsi}
                </span>
                <span className="text-[10px] text-slate-600">·</span>
                <span className="text-[10px] text-slate-500">
                  {categoryLabel(v.category)}
                </span>
              </div>
            </button>
          ))}
          {list.length === 0 && (
            <p className="text-center text-xs text-slate-500 py-6 font-mono">
              {results !== null ? "No matches" : "Awaiting vessels…"}
            </p>
          )}
          {list.length > 250 && (
            <p className="text-center text-[11px] text-slate-500 py-3 font-mono border-t border-slate-800/60">
              Showing 250 of {list.length} — refine with search or filters
            </p>
          )}
        </div>
      </div>
      </div>

      <div
        data-testid="source-references"
        className="shrink-0 px-3 py-2 border-t border-slate-800/80 bg-slate-950/40"
      >
        <p className="font-mono text-[9px] leading-relaxed text-slate-600">
          <span className="uppercase tracking-widest text-slate-500">Sources</span>{" "}
          · Vessels: AISstream · Rigs: Global Fishing Watch · Slicks: SkyTruth Cerulean (Sentinel-1, ESA) · Cables: OpenStreetMap (ODbL) &amp; public sources (indicative)
        </p>
      </div>
    </aside>
  );
};

export default LeftPanel;
